import { useMemo, useState } from "react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { NativeSelect } from "./crm-ui";
import { useCrmText, type CrmTextKey } from "@/lib/crm/i18n";
import { fieldLabel, type CrmEntity } from "@/lib/crm/fields";
import { useCrmAudit, type CrmMember } from "@/lib/crm/api";
import { displayAuditValue, formatDateTime } from "@/lib/crm/view-engine";
import { Loader2, RefreshCw } from "lucide-react";

/** Audit history tab: one row per changed field, newest first (like Dynamics "Audit History"). */
export function CrmAudit({ entity, recordId, members }: { entity: CrmEntity; recordId: string; members: CrmMember[] }) {
  const { lang, tx } = useCrmText();
  const { data, isLoading, isFetching, refetch } = useCrmAudit(entity, recordId);
  const [field, setField] = useState("");

  const memberNames = useMemo(() => Object.fromEntries(members.map((m) => [m.userId, m.name])), [members]);

  const rows = useMemo(() => {
    const out: Array<{ id: string; at: string; by: string; op: string; field: string; old: string; new: string }> = [];
    for (const e of data || []) {
      const changes = e.changes.length ? e.changes : [{ field: "", old: null, new: null }];
      changes.forEach((c, idx) => {
        out.push({ id: `${e.id}:${idx}`, at: e.changedAt, by: e.userName || "", op: e.operation, field: c.field, old: c.old ?? "", new: c.new ?? "" });
      });
    }
    return out;
  }, [data]);

  const fieldsInHistory = useMemo(() => [...new Set(rows.map((r) => r.field).filter(Boolean))], [rows]);
  const visible = field ? rows.filter((r) => r.field === field) : rows;

  const label = (key: string) => (key === "leadId" ? tx("leadIdField") : fieldLabel(entity, key, lang));
  const value = (key: string, v: string) => (key === "leadId" || key === "contactId" ? (v ? v.slice(0, 8) + "…" : "") : displayAuditValue(entity, key, v, lang, memberNames));
  const opLabel = (op: string) => {
    const key = `op_${op}` as CrmTextKey;
    const text = tx(key);
    return text === key ? op : text;
  };

  return (
    <div className="space-y-3" data-testid="audit-history">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <NativeSelect value={field} onChange={(e) => setField(e.target.value)} className="w-56" data-testid="audit-field-filter">
          <option value="">{tx("allFields")}</option>
          {fieldsInHistory.map((k) => <option key={k} value={k}>{label(k)}</option>)}
        </NativeSelect>
        <Button variant="outline" size="sm" onClick={() => refetch()}>
          <RefreshCw className={`h-4 w-4 ${isFetching ? "animate-spin" : ""}`} /> {tx("refresh")}
        </Button>
      </div>
      {isLoading ? (
        <div className="flex justify-center py-10 text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin" /></div>
      ) : visible.length === 0 ? (
        <p className="rounded-lg border py-10 text-center text-sm text-muted-foreground">{tx("noAudit")}</p>
      ) : (
        <div className="overflow-x-auto rounded-lg border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="whitespace-nowrap">{tx("changedOn")}</TableHead>
                <TableHead className="whitespace-nowrap">{tx("changedBy")}</TableHead>
                <TableHead>{tx("event")}</TableHead>
                <TableHead className="whitespace-nowrap">{tx("changedField")}</TableHead>
                <TableHead>{tx("oldValue")}</TableHead>
                <TableHead>{tx("newValue")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {visible.map((r) => (
                <TableRow key={r.id} data-testid="audit-row">
                  <TableCell className="whitespace-nowrap text-xs">{formatDateTime(r.at, lang)}</TableCell>
                  <TableCell className="whitespace-nowrap text-xs">{r.by}</TableCell>
                  <TableCell className="whitespace-nowrap text-xs">{opLabel(r.op)}</TableCell>
                  <TableCell className="whitespace-nowrap text-xs font-medium">{r.field ? label(r.field) : "—"}</TableCell>
                  <TableCell className="max-w-[16rem] break-words text-xs text-muted-foreground">{r.field ? value(r.field, r.old) || "—" : ""}</TableCell>
                  <TableCell className="max-w-[16rem] break-words text-xs">{r.field ? value(r.field, r.new) || "—" : ""}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
